import { AdminPanel, type KnobGroup, type Knob } from './admin';
import { CLIMATE, TERRAIN, BIOMES } from '../config';
import { biomeAt } from '../engine/biome';
import { terrainHeight } from '../engine/terrain';

// The F3 biome tool. A top-down biome-map preview + a legend, over the shared AdminPanel (climate +
// terrain-shape knobs, seed reroll, per-group randomize, JSON export of { climate, biomes }). Tuning a
// knob regenerates the world and (debounced) redraws the preview, so you can dial the biome layout live.

const NAMES = ['Ocean', 'Beach', 'Grassland', 'Forest', 'Meadow', 'Desert', 'Rocky', 'Hills', 'Mountain', 'Snow', 'Irradiated'];
// Distinct legend colours (NOT the in-game grass tints) so the map reads clearly. Index = Biome enum.
const LEGEND: ReadonlyArray<readonly [number, number, number]> = [
  [38, 92, 150],   // Ocean
  [226, 211, 160], // Beach
  [108, 165, 78],  // Grassland
  [52, 98, 48],    // Forest
  [150, 200, 92],  // Meadow
  [226, 200, 120], // Desert
  [142, 142, 148], // Rocky
  [128, 160, 70],  // Hills
  [108, 113, 120], // Mountain
  [236, 242, 248], // Snow
  [172, 200, 72],  // Irradiated
];

export function createBiomeAdmin(regenerate: () => void): AdminPanel {
  const map = makeBiomeMap();
  let timer = 0;
  const redrawSoon = (): void => { clearTimeout(timer); timer = window.setTimeout(() => map.redraw(), 220); };
  const onChange = (): void => { regenerate(); redrawSoon(); };

  const k = (label: string, obj: Record<string, number>, key: string, min: number, max: number, step: number, help?: string): Knob =>
    ({ label, help, get: () => obj[key], set: (v) => { obj[key] = v; }, min, max, step, onChange });

  // NOTE: each knob's [min,max] also bounds the per-group RANDOM roll, so the ranges are kept to the
  // proven-good zone -- a Random roll (or a slider) stays in worlds that look right (multiple distinct
  // ranges, tall-but-sane peaks, rolling lowlands) instead of degenerate ones (no mountains / one massif /
  // dead flat). 'seed' rerolls layout freely within the good shape.
  const groups: KnobGroup[] = [
    {
      title: 'Climate', knobs: [
        k('seed', CLIMATE, 'seed', 1, 99999, 1, 'reroll the biome layout'),
        k('temp freq', CLIMATE, 'tempFreq', 0.14, 0.44, 0.01, 'smaller = bigger temperature regions'),
        k('moist freq', CLIMATE, 'moistFreq', 0.16, 0.50, 0.01, 'smaller = bigger wet/dry regions'),
        k('forest thresh', CLIMATE, 'forestThresh', 0.42, 0.60, 0.01, 'lower = more forest'),
        k('irrad freq', CLIMATE, 'irradFreq', 0.08, 0.30, 0.01, 'smaller = fewer, larger blight patches'),
        k('irrad thresh', CLIMATE, 'irradThresh', 0.85, 0.98, 0.005, 'quantile: 0.93 = top 7% of land is irradiated'),
        k('alt cooling', CLIMATE, 'altCooling', 0.003, 0.010, 0.001, 'how fast altitude turns to snow'),
      ],
    },
    {
      title: 'Terrain shape', knobs: [
        k('mountains', TERRAIN, 'mountainThresh', 0.56, 0.66, 0.01, 'BIG ranges: lower = more/merged, higher = sparser'),
        k('peak height', TERRAIN, 'peakAmp', 90, 150, 5, 'tall climbable ranges'),
        k('range size', TERRAIN, 'bigMtnFreq', 0.28, 0.46, 0.02, 'lower = fewer, larger ranges'),
        k('small peaks', TERRAIN, 'smallMountainThresh', 0.60, 0.72, 0.01, 'higher = fewer scattered knolls'),
        k('small peak ht', TERRAIN, 'peakAmpSmall', 30, 60, 5, 'height of the scattered smaller peaks'),
        k('small peak size', TERRAIN, 'smallMtnFreq', 0.7, 1.2, 0.05, 'higher = more, smaller, scattered'),
        k('mound height', TERRAIN, 'moundAmp', 4, 15, 1, 'patchy 4-15m micro-relief (walkable)'),
        k('mound size', TERRAIN, 'moundFreq', 0.8, 1.4, 0.05, 'bigger = wider, gentler mounds'),
        k('rugged regions', TERRAIN, 'ruggedThresh', 0.42, 0.6, 0.02, 'higher = more flat meadow, fewer bumpy regions'),
        k('rugged size', TERRAIN, 'ruggedFreq', 0.2, 0.4, 0.02, 'size of the bumpy-vs-flat patches'),
        k('outcrops', TERRAIN, 'outcropThresh', 0.70, 0.80, 0.01, 'higher = rarer rocky knolls'),
        k('outcrop height', TERRAIN, 'outcropAmp', 10, 25, 1, 'dramatic rocky mini-mesas'),
        k('amplitude', TERRAIN, 'amplitude', 38, 56, 1, 'rolling-hill height'),
        k('snow start', TERRAIN, 'snowStart', 34, 56, 1),
        k('snow end', TERRAIN, 'snowEnd', 60, 95, 1),
      ],
    },
  ];

  return new AdminPanel(groups, {
    title: 'OXIDE BIOMES',
    topEl: map.el,
    exportJSON: () => JSON.stringify({ climate: CLIMATE, biomes: BIOMES }, null, 2),
    importJSON: (text) => {
      const o = JSON.parse(text) as { climate?: Record<string, number>; biomes?: unknown[] };
      if (o.climate) Object.assign(CLIMATE, o.climate);
      if (Array.isArray(o.biomes)) {
        for (let i = 0; i < BIOMES.length && i < o.biomes.length; i++) Object.assign(BIOMES[i], o.biomes[i]);
      }
      regenerate();
      map.redraw();
    },
  });
}

function makeBiomeMap(): { el: HTMLElement; redraw: () => void } {
  const SIZE = 192;
  const wrap = document.createElement('div');
  wrap.style.cssText = 'margin-bottom:8px';
  const canvas = document.createElement('canvas');
  canvas.width = SIZE; canvas.height = SIZE;
  canvas.style.cssText = 'width:100%;image-rendering:pixelated;border:1px solid rgba(120,140,160,0.3);border-radius:4px;display:block';
  const ctx = canvas.getContext('2d');
  wrap.appendChild(canvas);

  const leg = document.createElement('div');
  leg.style.cssText = 'display:flex;flex-wrap:wrap;gap:3px 8px;margin-top:5px;font-size:10px;color:#9aa7b5';
  for (let i = 1; i < NAMES.length; i++) {
    const c = LEGEND[i];
    const item = document.createElement('span');
    item.style.cssText = 'display:flex;align-items:center;gap:3px';
    item.innerHTML = `<span style="width:9px;height:9px;border-radius:2px;background:rgb(${c[0]},${c[1]},${c[2]})"></span>${NAMES[i]}`;
    leg.appendChild(item);
  }
  wrap.appendChild(leg);

  const redraw = (): void => {
    if (!ctx) return;
    const R = TERRAIN.islandOuter;
    const img = ctx.createImageData(SIZE, SIZE);
    for (let py = 0; py < SIZE; py++) {
      for (let px = 0; px < SIZE; px++) {
        const x = (px / (SIZE - 1) * 2 - 1) * R;
        const z = (py / (SIZE - 1) * 2 - 1) * R;
        const b = biomeAt(x, z, terrainHeight(x, z));
        const c = LEGEND[b];
        const o = (py * SIZE + px) * 4;
        img.data[o] = c[0]; img.data[o + 1] = c[1]; img.data[o + 2] = c[2]; img.data[o + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  };
  redraw();
  return { el: wrap, redraw };
}
